import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/core/firebase/client'
import { logger } from '@upadelmatch/core'
import { markUpdateAttempt } from './update-attempt'

/** Injected at build time from package.json (vite.config define). */
export const APP_VERSION = import.meta.env.VITE_APP_VERSION as string

// vite-plugin-pwa's updateSW — registered from main.tsx. Calling it with
// `true` tells the WAITING service worker to skipWaiting + reload, so the
// update banner actually delivers the new bundle (a bare location.reload()
// would re-serve the old precache and loop the banner forever).
let swUpdate: ((reload?: boolean) => Promise<void>) | null = null
export function registerSwUpdater(fn: (reload?: boolean) => Promise<void>): void {
  swUpdate = fn
}

/**
 * The service worker's OWN "a new bundle is waiting" signal.
 *
 * `useUpdateAvailable` below compares version NUMBERS from Firestore, which
 * silently misses every deploy that ships new code under the same version —
 * and those happen: v0.1.27 named two different production bundles a month
 * apart (see docs/releases/usersmatch-pwa-v0.1.27.md), and every dev redeploy
 * reuses its version by design. In that state `newerThan()` is false, the
 * banner never shows, and because registration is 'prompt' (never
 * auto-activating), a returning visitor is pinned to their cached shell
 * indefinitely. Reproduced 2026-08-09: a phone opening a share link rendered a
 * months-old bundle while the server served the current one.
 *
 * That is worst exactly where it hurts most — the share door is how strangers
 * arrive, and it is the one screen where serving a stale app costs a player.
 *
 * This signal has no such blind spot: workbox fires it when a new worker is
 * installed and waiting, which is true precisely when there IS a new bundle,
 * whatever the version says.
 */
let swWaiting = false
const swWaitingListeners = new Set<() => void>()
export function markSwWaiting(): void {
  swWaiting = true
  for (const l of swWaitingListeners) l()
}

/** Banner-driving companion to `useUpdateAvailable`; either one is enough. */
export function useSwUpdateWaiting(): boolean {
  const [waiting, setWaiting] = useState(swWaiting)
  useEffect(() => {
    if (swWaiting) {
      setWaiting(true)
      return
    }
    const listener = () => setWaiting(true)
    swWaitingListeners.add(listener)
    return () => {
      swWaitingListeners.delete(listener)
    }
  }, [])
  return waiting
}
/**
 * Hard-refresh past every cache layer: unregister every service worker, delete
 * every Cache Storage entry, then `location.replace()` with a cache-busting
 * param (replace, not assign, so the pre-reload URL never enters history).
 *
 * This used to be `updateSW(true)` plus a 10 s `location.reload()` fallback,
 * on the reasoning that after skipWaiting a plain reload is served by the new
 * worker. That holds when a new worker actually reaches the waiting state. It
 * does NOT recover a client already pinned to a stale precache — measured
 * 2026-08-09, where a browser kept rendering a months-old shell while the
 * server served the current one, and only an explicit unregister + cache wipe
 * fixed it. A reload is what the old worker intercepts.
 *
 * Ported from clubmanager's `core/version/force-update.ts`, which had learned
 * both refinements below the hard way:
 *  - the cleanup is capped by a deadline, so a hung `getRegistrations()` can
 *    never swallow the tap — navigation fires regardless;
 *  - `window.caches` is checked by VALUE, because Safari private browsing has
 *    `'caches' in window === true` with `window.caches === null`.
 */
const UNREGISTER_DEADLINE_MS = 3000
const CLEANUP_DEADLINE_MS = 3000

export function applyUpdate(): void {
  // Remember we tried, BEFORE navigating — see update-attempt.ts. Without
  // this, a reload that lands on the same bundle re-fires the banner forever.
  markUpdateAttempt(APP_VERSION)
  // Best-effort graceful path first: if a worker really is waiting, this lets
  // it take over without a full asset refetch. It is never relied upon.
  if (swUpdate) void swUpdate(true)

  // UNREGISTRATION GETS ITS OWN, GENEROUS BOUND — it is the step that decides
  // whether the reload lands on a clean slate, so it must not share a 3 s
  // budget with optional cache clearing.
  const cleanup = (async () => {
    // Its OWN budget, not one shared with the cache wipe. Sharing was the bug:
    // a slow cache clear could eat the whole allowance, the unregister lost the
    // race, and the page reloaded with the old worker still registered — the
    // new one waiting behind it is what re-fires this banner. Still bounded,
    // because `getRegistrations()` hangs forever on some Chrome builds and a
    // click that does NOTHING is worse than a banner seen twice.
    const unregister = (async () => {
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations()
          await Promise.all(regs.map((r) => r.unregister()))
        }
      } catch (err) {
        logger.w('[version] could not unregister service workers', err)
      }
    })()
    await Promise.race([
      unregister,
      new Promise<void>((resolve) => setTimeout(resolve, UNREGISTER_DEADLINE_MS)),
    ])
    const clearCaches = (async () => {
      try {
        if (window.caches && typeof window.caches.keys === 'function') {
          const keys = await window.caches.keys()
          await Promise.all(keys.map((k) => window.caches.delete(k)))
        }
      } catch (err) {
        logger.w('[version] could not clear caches', err)
      }
    })()
    await Promise.race([
      clearCaches,
      new Promise<void>((resolve) => setTimeout(resolve, CLEANUP_DEADLINE_MS)),
    ])
  })()

  void cleanup.then(() => {
    const { origin, pathname } = window.location
    window.location.replace(`${origin}${pathname}?_v=${Date.now()}`)
  })
}

function newerThan(latest: string, current: string): boolean {
  const a = latest.split('.').map(Number)
  const b = current.split('.').map(Number)
  for (let i = 0; i < 3; i++) {
    if ((a[i] ?? 0) > (b[i] ?? 0)) return true
    if ((a[i] ?? 0) < (b[i] ?? 0)) return false
  }
  return false
}

/**
 * True when the deployed `platform_config/app_versions.usersmatch.latest`
 * is ahead of this bundle — the App shell shows the update banner and a
 * reload picks up the new SW + assets. Subscribes post-auth only (the doc is
 * signed-in readable, same rule as the admins doc).
 */
export function useUpdateAvailable(enabled: boolean): boolean {
  const [behind, setBehind] = useState(false)
  useEffect(() => {
    if (!enabled) return
    return onSnapshot(
      doc(db, 'platform_config', 'app_versions'),
      (snap) => {
        const app = snap.data()?.['usersmatch'] as
          | { latest?: string; minRequired?: string }
          | undefined
        const latest = app?.latest
        if (!latest || !newerThan(latest, APP_VERSION)) return

        // FORCED tier. `minRequired` defaults to '0.0.0', i.e. never forces —
        // a parked lever, exactly as clubmanager keeps it. Raising it is how a
        // fix reaches everyone without depending on players noticing a banner,
        // and this app has already shipped one release where that mattered
        // (the B1 result-forgery fix, which rewrote other people's public
        // reputation while it was live).
        //
        // Deliberately NOT the default for every deploy: applying silently
        // means reloading a player mid-action, and the destructive cases here
        // are real — the Crear partido form, the name field on the share door,
        // the result sheet. clubmanager reverted exactly that behaviour ("every
        // operator click flashed the splash") and split it into two tiers. Same
        // split here, same reason.
        const minRequired = app.minRequired ?? '0.0.0'
        if (newerThan(minRequired, APP_VERSION)) {
          logger.i(`forced update: ${APP_VERSION} < minRequired ${minRequired} — applying`)
          applyUpdate()
          return
        }

        logger.i(`update available: ${APP_VERSION} → ${latest}`)
        setBehind(true)
      },
      () => {
        /* permission/offline — stay quiet; next session retries */
      },
    )
  }, [enabled])
  return behind
}
